import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Hub } from "../lib/dados";

// As peças compartilhadas pelas ferramentas que MORAM no hub.
//
// ---------------------------------------------------------------------------
// Por que este arquivo existe: três ferramentas com a mesma cara "simples"
// ---------------------------------------------------------------------------
// Em 26/08 Victor disse que o Gerador de Currículos, o MEI PRO e o IR PRO
// estavam "com o design muito simples" e pediu que ficassem como o HUB DP e o
// Calendário. As capturas do antes (`.capturas/antes/`) mostram o mesmo
// diagnóstico da primeira versão do HUB DP: **nenhuma das três usava as peças
// bonitas que o hub já tem** — eram título, subtítulo cinza, uma caixa branca e
// o rodapé. Zero `painel-navy`, zero `tv-card`, zero `rise`, zero `porta`.
//
// A `Cabine` abaixo é a peça que faltava em comum: o painel navy de entrada da
// ferramenta, com trilha, título, descrição e um lugar para FATOS REAIS em
// cartões de vidro. Ela é irmã do `HeroModulo` do HUB DP e da cabine do
// Calendário, e não uma cópia: aquelas carregam decisões específicas (a trilha
// do HUB DP volta ao índice dos cinco módulos; a do Calendário tem a navegação
// de mês). Esta é a forma genérica — trilha e fatos vêm de quem chama.
//
// ---------------------------------------------------------------------------
// Sobre navy, cor semântica é armadilha
// ---------------------------------------------------------------------------
// Dentro de `painel-navy` e `tv-card` o fundo é escuro NOS DOIS TEMAS. Aqui só
// entram `text-white`, `navy-ink`, `navy-mute`, `laranja-claro` e cores fixas —
// nunca `tinta`, `cinza` ou `titulo`, que clareiam no escuro e somem. A
// varredura de classe das provas de tela mede isso.
//
// ---------------------------------------------------------------------------
// Fatos, nunca enfeite
// ---------------------------------------------------------------------------
// "Nada de progresso, streak ou heatmap" está no CLAUDE.md: widget alimentado
// por dado que não existe destrói a credibilidade dos que têm dado real. Todo
// número que entra num `TileDeFato` aqui é medido — quantos currículos o aluno
// tem, quantas áreas o conteúdo tem, quantos clientes estão acima de 80% do
// teto. Se não há dado, o tile não aparece; não se inventa um.

/**
 * A cabine de uma ferramenta: o painel navy de entrada.
 *
 * `fatos` é o lugar dos cartões de vidro (use `TileDeFato` de `CalendarioUi`,
 * importado — não copiado). `acao` é o CTA principal, em pill laranja.
 * `rodape` é uma faixa opcional na base do painel — a Ocupações põe a busca ali,
 * porque a busca É a ferramenta.
 */
export function Cabine({
  trilha,
  rotulo,
  titulo,
  descricao,
  fatos,
  acao,
  rodape,
  children,
}: {
  /** O caminho de volta, quando há um (ex.: "MEI PRO" → /ferramenta/mei-pro). */
  trilha?: { para: string; rotulo: string };
  /** A etiqueta pequena acima do título ("FERRAMENTA", "LIVRO-CAIXA"…). */
  rotulo?: string;
  titulo: ReactNode;
  descricao?: ReactNode;
  fatos?: ReactNode;
  acao?: ReactNode;
  rodape?: ReactNode;
  children?: ReactNode;
}) {
  // Os fatos em FAIXA, e não numa coluna ao lado do título.
  //
  // A primeira versão punha `fatos` num grid `w-auto` à direita: com três tiles
  // e `auto-fit`, o grid encolhia ao mínimo e empilhava os três numa coluna —
  // deixando um vazio de meia tela no meio do navy, visível na captura do
  // Simulador em 26/08. Um fato ao lado do título funciona (é o `HeroModulo` do
  // HUB DP); três pedem a largura toda, como a linha de fatos do HUB DP e a
  // cabine do Calendário fazem.
  //
  // `auto-fit` com `minmax` continua: com um fato só ele ocupa a faixa inteira,
  // e é por isso que quem tem um herói grande (as rotinas do Simulador) não
  // precisa de layout próprio.
  return (
    <header className="painel-navy rounded-cf-lg px-5 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0 flex-1">
          {(trilha || rotulo) && (
            <nav aria-label="Trilha" className="text-xs tracking-[0.14em] text-navy-mute uppercase">
              {trilha && (
                <>
                  <Link
                    to={trilha.para}
                    className="underline decoration-navy-mute/40 hover:text-navy-ink"
                  >
                    {trilha.rotulo}
                  </Link>
                  <span aria-hidden="true" className="mx-1.5">
                    ›
                  </span>
                </>
              )}
              {rotulo && <span className="text-laranja-claro">{rotulo}</span>}
            </nav>
          )}
          <h1 className="rise mt-2 font-display text-3xl font-bold text-white sm:text-4xl">
            {titulo}
          </h1>
          {descricao && (
            <p
              className="rise mt-2 max-w-2xl text-sm leading-relaxed text-navy-mute sm:text-base"
              style={{ "--i": 1 } as React.CSSProperties}
            >
              {descricao}
            </p>
          )}
          {acao && (
            <div className="rise mt-5" style={{ "--i": 2 } as React.CSSProperties}>
              {acao}
            </div>
          )}
        </div>

      </div>

      {fatos && (
        <div
          className="rise mt-6 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]"
          style={{ "--i": 3 } as React.CSSProperties}
        >
          {fatos}
        </div>
      )}

      {children}

      {rodape && (
        <div
          className="rise mt-6 border-t border-navy-mute/20 pt-5"
          style={{ "--i": 3 } as React.CSSProperties}
        >
          {rodape}
        </div>
      )}
    </header>
  );
}

/**
 * Para qual CURSO esta ferramenta volta — e o nome dele.
 *
 * Victor, em 26/08: *"em todas as ferramentas deve ter o botão de voltar para o
 * curso"*. Antes só três telas tinham, e as três com o mesmo texto CRAVADO
 * ("Voltar à Escola") — o que é falso para quem abriu o HUB DP pela Pós DP ou o
 * Calendário pela Pós RT. O mesmo card serve cursos diferentes desde a `000064`,
 * e o botão de voltar tem de dizer de onde a pessoa veio.
 *
 * A precedência: **a query `?curso=` → o único curso do card → nada.**
 *
 *  1. `?curso=` é o que `aberturaDoCard` põe no link para todo card de escopo
 *     'curso' (`lib/dados.ts`). É a resposta certa, porque é o caminho que o
 *     aluno percorreu;
 *  2. sem a query (rota digitada, link antigo, favorito), se o card pertence a um
 *     curso só, é aquele — não há ambiguidade a resolver;
 *  3. em card de dois cursos sem query, `null`: mandar para o curso errado é pior
 *     que não oferecer o atalho, e o Topo do hub já leva à inicial.
 *
 * Devolve o NOME do curso do aluno, não o slug, porque é ele que vai no botão.
 */
export type Volta = { para: string; rotulo: string };

export function cursoDeVolta(hub: Hub, cardSlug: string, cursoDaQuery: string | null): Volta | null {
  const card = [...hub.cardsDeCurso, ...hub.cardsDeConta].find((c) => c.card_slug === cardSlug);
  const slug =
    cursoDaQuery && card?.por_cursos.includes(cursoDaQuery)
      ? cursoDaQuery
      : card?.por_cursos.length === 1
        ? card.por_cursos[0]
        : null;
  if (!slug) return null;

  // Nos DOIS conjuntos: `hub.cursos` são os livres e `hub.faculdade` são as pós e
  // o MBA — e três ferramentas internas abrem por uma pós (o HUB DP pela Pós DP,
  // o Calendário pela Pós RT e pela Pós Rotinas, o Simulador pela Pós Rotinas).
  // Procurar só em `cursos` fazia o botão dizer "Voltar para o curso", genérico,
  // exatamente para quem mais precisa saber de onde veio. Visto na captura.
  const curso =
    hub.cursos.find((c) => c.slug === slug) ?? hub.faculdade.find((c) => c.slug === slug);
  return { para: `/curso/${slug}`, rotulo: `Voltar para ${curso?.nome ?? "o curso"}` };
}

/**
 * A barra de volta ao curso, acima do conteúdo da ferramenta.
 *
 * Desenhada UMA vez, na `Moldura` de `Ferramenta.tsx`, e não em cada tela: era
 * assim que as três que tinham o botão divergiam do resto, e é o que garante que
 * a próxima ferramenta interna nasça com ele. Fora do painel navy de propósito —
 * é navegação do hub, não da ferramenta.
 *
 * A `acao`, à direita, é o botão «Avaliar esta ferramenta» (000326): na mesma
 * barra, e não em cada tela, pela mesma razão do botão de voltar. Sem `volta`
 * (o card de dois cursos aberto sem a query), a barra continua existindo por
 * causa dela.
 */
export function BarraDeVolta({
  volta,
  largura = "max-w-6xl",
  acao = null,
}: {
  volta: Volta | null;
  /** A largura da MOLDURA — a barra acompanha o conteúdo e o rodapé. */
  largura?: string;
  acao?: ReactNode;
}) {
  if (!volta && !acao) return null;
  return (
    <div className={`mx-auto flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 pt-5 sm:px-6 ${largura}`}>
      {/* `truncate` com largura máxima, e o texto inteiro no `title`: o nome de
          uma pós tem 74 caracteres ("Pós-graduação em Departamento Pessoal e
          Legislação Trabalhista"), e sem o corte o botão de voltar ocupava três
          linhas no celular. O leitor de tela recebe o rótulo completo. */}
      {volta ? (
        <Link
          to={volta.para}
          data-voltar-ao-curso
          title={volta.rotulo}
          className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm font-semibold text-link hover:underline"
        >
          <span aria-hidden="true" className="shrink-0">
            ←
          </span>
          <span className="truncate">{volta.rotulo}</span>
        </Link>
      ) : (
        <span />
      )}
      {acao}
    </div>
  );
}

/**
 * O numeral de revista: "01", "02"… em fonte de display, para dar identidade a
 * uma lista de áreas sem transformá-la em grade de cards. A cicatriz do IR Pro
 * diz que lista densa encontra melhor que grade — o numeral é o que deixa a
 * lista densa E bonita.
 */
export function Numeral({ n, className = "" }: { n: number; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`font-display text-2xl font-bold tabular-nums leading-none text-laranja-texto/70 ${className}`}
    >
      {String(n).padStart(2, "0")}
    </span>
  );
}

/**
 * A barra segmentada de "N de M partes": um segmento por parte, aceso ou
 * apagado. Mais honesta que uma barra contínua para contagens pequenas — 6 de 8
 * é seis blocos acesos e dois apagados, e o olho vê QUAIS faltam pela posição.
 *
 * Funciona sobre navy e fora dele: os segmentos apagados usam branco a 15%, que
 * é visível sobre navy e quase invisível sobre superfície clara — por isso quem
 * a usa fora do navy passa `sobreNavy={false}`.
 *
 * Com `mapa`, cada segmento é UMA parte específica — o segmento i acende se
 * `mapa[i]` for verdadeiro —, e a promessa do parágrafo acima vale ao pé da
 * letra: o aluno que concluiu as aulas 1, 2 e 7 vê três blocos acesos NAS
 * posições 1, 2 e 7, não os três primeiros. Sem `mapa`, acendem os `acesos`
 * primeiros, como sempre (as partes preenchidas do currículo não têm posição).
 */
export function Segmentos({
  total,
  acesos,
  rotulo,
  sobreNavy = true,
  mapa,
}: {
  total: number;
  acesos: number;
  rotulo: string;
  sobreNavy?: boolean;
  /** Qual segmento acende, por posição. Quando dado, `acesos` só entra no rótulo. */
  mapa?: boolean[];
}) {
  const aceso = (i: number) => (mapa ? mapa[i] === true : i < acesos);
  return (
    <div
      role="img"
      aria-label={`${rotulo}: ${acesos} de ${total}`}
      className="flex gap-1"
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          data-aceso={aceso(i) ? "sim" : "nao"}
          className={`h-1.5 flex-1 rounded-full ${
            aceso(i)
              ? "bg-laranja"
              : sobreNavy
                ? "bg-white/15"
                : "bg-borda"
          }`}
        />
      ))}
    </div>
  );
}

/**
 * Uma porta grande para outra tela da mesma ferramenta — o card inteiro é o
 * link, com a soleira laranja da utility `porta` e um fato ao lado do título.
 * É como a inicial desenha as portas dos cursos (`surface porta rise`), e é a
 * peça que o MEI PRO usa para a gestão e as ocupações.
 */
export function PortaGrande({
  para,
  titulo,
  descricao,
  cta = "Abrir",
  fato,
  atencao = false,
  icone,
  i = 0,
}: {
  para: string;
  titulo: string;
  descricao: string;
  /** O verbo da porta. As provas de tela procuram por ele, então é texto visível. */
  cta?: string;
  /** Um fato real ao lado do título ("3 clientes"). Omitido quando não há. */
  fato?: ReactNode;
  /** O fato pede atenção (cliente acima do teto, prazo vencendo): sai em alerta. */
  atencao?: boolean;
  icone?: ReactNode;
  i?: number;
}) {
  return (
    <Link
      to={para}
      className="surface porta rise flex min-h-36 flex-col gap-3 p-5 sm:p-6"
      style={{ "--i": i } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {icone && (
            <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-tile text-laranja-texto">
              {icone}
            </span>
          )}
          <h2 className="font-display text-lg font-bold text-titulo">{titulo}</h2>
        </div>
        {fato && (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
              atencao ? "bg-alerta-fundo text-alerta-tinta" : "bg-chip text-chip-tinta"
            }`}
          >
            {fato}
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed text-cinza">{descricao}</p>
      <span className="mt-auto inline-flex items-center gap-2 text-sm font-bold text-link">
        {cta}
        <span aria-hidden="true" className="text-laranja-texto">
          →
        </span>
      </span>
    </Link>
  );
}
